import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder,  FormControl,  FormGroup,  ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ChangeTracker } from 'src/app/common/classes/change-tracker.class';
import { EntityManagementLayoutComponent } from 'src/app/common/layouts/entity-management-layout.component';
import { DropdownComponent } from '../../core/dropdown/dropdown.component';
import { InputComponent } from '../../core/input/input.component';

@Component({
    selector: 'app-signature-settings',
    standalone: true,
    templateUrl: './signature-settings.component.html',
    styleUrls: ['./signature-settings.component.scss'],
    imports: [CommonModule, EntityManagementLayoutComponent, ReactiveFormsModule, InputComponent, MatCheckboxModule, DropdownComponent, MatFormFieldModule]
})
export class SignatureSettingsComponent implements OnInit {
  readonly title = 'Preferences';
  readonly hours = ['Select', '12:00', '1:00', '2:00', '3:00', '4:00', '5:00', '6:00', '7:00', '8:00', '9:00', '10:00', '11:00'];
  readonly meridians = ['Select', 'AM', 'PM'];
  readonly days = ['Select', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  readonly pinErrors: Record<string, string> = {
    'pattern': 'Please enter a 6-digit pin to continue.',
    'required': 'Please enter "Enter Pin" to continue.'
  };
  readonly pinConfirmationErrors: Record<string, string> = {
    'pattern': 'Please enter a 6-digit pin to continue.',
    'required': 'Please enter "Confirm Pin" to continue.'
  };

  @Input() settingsConfiguration!: SettingsConfiguration;
  @Input() formGroup!: FormGroup;
  @Output() saved: EventEmitter<void> = new EventEmitter();

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.buildForm();
  }

  private buildForm(): void {
    Object.entries({
      pin: this.formBuilder.control(null, Validators.pattern('^\\d{6}$')),
      pinConfirmation: this.formBuilder.control(null, Validators.pattern('^\\d{6}$')),
      isSendEverySignatureRequest: this.formBuilder.control(false),
      isSendEveryDay: this.formBuilder.control(true),
      sendEveryDayTime: this.formBuilder.control({ value: this.hours[6], disabled: true }),
      sendEveryDayMeridian: this.formBuilder.control({ value: this.meridians[1], disabled: true }),
      isSendEveryWeek: this.formBuilder.control(false),
      sendEveryWeekTime: this.formBuilder.control({ value: this.hours[0], disabled: true }),
      sendEveryWeekMeridian: this.formBuilder.control({ value: this.meridians[0], disabled: true }),
      sendEveryWeekDay: this.formBuilder.control({ value: this.days[0], disabled: true }),
    }).forEach(([controlKey, control]) => this.formGroup.registerControl(controlKey, control));
    this.formGroup.addValidators([
      this.pinValidator(),
      this.atLeastOneSendCadence(),
      this.isEveryWeekValidate(),
      this.isEveryDayValidate()
    ]);
  }

  private pinValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const haveValuesBeenUpdated = control?.get('pin')?.dirty && control?.get('pinConfirmation')?.dirty;
      const areValuesValid = control?.get('pin')?.valid && control?.get('pinConfirmation')?.valid;
      const areValuesDifferent = control?.get('pin')?.value !== control?.get('pinConfirmation')?.value
      if (haveValuesBeenUpdated && areValuesValid && areValuesDifferent) {
        return { 'pin-mismatch': true};
      }
      return null;
    }
  }

  private atLeastOneSendCadence(): ValidatorFn {
    return (control: AbstractControl) => {
      if (control?.get('isSendEverySignatureRequest')?.value || control?.get('isSendEveryDay')?.value || control?.get('isSendEveryWeek')?.value) {
        return null;
      }
      return { 'at-least-one-send': true};
    }
  }
  private isEveryDayValidate(): ValidatorFn {
    return (control: AbstractControl) => {
      if (control?.get('isSendEveryDay')?.value) {
        if(control?.get('sendEveryDayTime')?.value === "Select" || control?.get('sendEveryDayMeridian')?.value === "Select"){
          return { 'isEveryDayValidate': true};
        }
      }
      return null;
    }
  }

  private isEveryWeekValidate(): ValidatorFn {
    return (control: AbstractControl) => {
      if (control?.get('isSendEveryWeek')?.value) {
        if(control?.get('sendEveryWeekTime')?.value === "Select" || control?.get('sendEveryWeekMeridian')?.value === "Select" || control?.get('sendEveryWeekDay')?.value === "Select"){
          return { 'isEveryWeekValidate': true};
        }
      }
      return null;
    }
  }

  updateEveryWeekControlStatus(shouldEnable: boolean): void {
    if (!shouldEnable) {
      this.formGroup.get('sendEveryWeekTime')?.disable();
      this.formGroup.get('sendEveryWeekMeridian')?.disable();
      this.formGroup.get('sendEveryWeekDay')?.disable();
      this.formGroup.get('sendEveryWeekTime')?.setValue(this.hours[0]);
      this.formGroup.get('sendEveryWeekMeridian')?.setValue(this.meridians[0]);
      this.formGroup.get('sendEveryWeekDay')?.setValue(this.days[0]);
    } else {
      this.formGroup.get('sendEveryWeekTime')?.enable();
      this.formGroup.get('sendEveryWeekMeridian')?.enable();
      this.formGroup.get('sendEveryWeekDay')?.enable();
    }
  }

  updateEveryDayControlStatus(shouldEnable: boolean): void {
      if (!shouldEnable) {
        this.formGroup.get('sendEveryDayTime')?.disable();
        this.formGroup.get('sendEveryDayMeridian')?.disable();
        this.formGroup.get('sendEveryDayTime')?.setValue(this.hours[0]);
        this.formGroup.get('sendEveryDayMeridian')?.setValue(this.meridians[0]);
      } else {
        this.formGroup.get('sendEveryDayTime')?.enable();
        this.formGroup.get('sendEveryDayMeridian')?.enable();
        this.formGroup.get('sendEveryDayTime')?.setValue(this.hours[6]);
        this.formGroup.get('sendEveryDayMeridian')?.setValue(this.meridians[1]);
      }
  }

  onSave(): void {
    this.saved.emit();
  }
}

export interface SettingsConfiguration {
  header: string;
  instructions: string;
  saveButtonText: string;
}